import { Divider, Stack } from '@mui/material';
import { getContacts, IContacts, IDistrictInfo } from 'API/contacts';
import { CustomMetaTitle } from 'Components/CustomMetaTitle';
import { LinkReturnToMain } from 'Components/LinkReturnToMain';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useAppSelector, useDictionary, useIsDesktop } from 'Hooks';
import { useCallback, useEffect, useRef, useState } from 'react';
import { GeneralInfo } from './GeneralInfo';
import { LocationInfo } from './LocationInfo';
import { PersonalTeamTitle } from './PersonalTeamTitle';
import { useLoader } from 'Hooks/useLoader';
import { ContactMember } from './ContactMember';
import { ContactCard } from './ContactCard';
import { useSearchParams } from 'react-router-dom';

export const PersonalTeam = () => {
  const imposterId = useAppSelector(
    ({ imposter: { imposterId } }) => imposterId
  );
  const [distAndContacts, setDistAndContacts] = useState<{
    contacts: IContacts[];
    districtInfo: IDistrictInfo;
  } | null>(null);

  const [searchParams] = useSearchParams();

  const scrollToRef = useRef<any>(null);
  const cardLinkFocusRef = useRef<any>(null);

  const { isLoading, setIsLoading, loader } = useLoader();
  const dictionary = useDictionary(Dictionary, DICTIONARY.PersonalTeam);
  const isDesktop = useIsDesktop();

  const initPersonalTeam = useCallback(async () => {
    setIsLoading(true);
    const result = await getContacts();
    setDistAndContacts(result ?? null);
    setIsLoading(false);
  }, [setIsLoading]);

  const initScrollHandler = useCallback(() => {
    if (searchParams.get('Contact') && scrollToRef.current) {
      setTimeout(() => {
        const elementTop =
          scrollToRef.current.getBoundingClientRect().top + window.scrollY;
        window.scrollTo({
          behavior: 'smooth',
          top: elementTop + -100,
        });
      }, 500);
    }
  }, [searchParams]);

  useEffect(() => {
    initPersonalTeam();
    initScrollHandler();
  }, [initPersonalTeam, initScrollHandler, imposterId]);

  if (isLoading) return loader;

  const haveContacts =
    distAndContacts?.contacts && distAndContacts?.contacts.length > 0
      ? true
      : false;

  return (
    <Stack gap={'32px'}>
      <CustomMetaTitle title={Dictionary[DICTIONARY.PersonalTeam].title} />
      <LinkReturnToMain link='/' />
      {distAndContacts && (
        <Stack gap={'48px'} maxWidth={'920px'}>
          <PersonalTeamTitle />
          <LocationInfo
            district={distAndContacts?.districtInfo}
            haveContacts={haveContacts}
          />
          {distAndContacts.contacts && (
            <Stack gap={'16px'}>
              {distAndContacts.contacts.map(
                (member: IContacts, index: number) => (
                  <ContactMember member={member} key={index} />
                )
              )}
            </Stack>
          )}
          <Divider
            aria-hidden='true'
            sx={{ borderColor: 'primary.shade_200' }}
            ref={scrollToRef}
          />
          <GeneralInfo
            districtName={distAndContacts.districtInfo?.name}
            districtId={distAndContacts.districtInfo?.districtId}
            urlName={distAndContacts.districtInfo?.urlName}
          />
          <Stack
            direction={'row'}
            flexWrap={'wrap'}
            justifyContent={isDesktop ? 'center' : 'flex-end'}
            gap={isDesktop ? '73px 24px' : '16px'}
            marginTop={'48px'}
          >
            {dictionary.contactBoxes.map((contactBox, index: number) => (
              <ContactCard
                img={contactBox.img}
                subtitle={contactBox.subtitle}
                title={contactBox.title}
                key={index}
                href={contactBox.href}
                cardLinkFocusRef={
                  index === 0 && searchParams.get('Contact') === '1'
                    ? cardLinkFocusRef
                    : null
                }
              />
            ))}
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};
